import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Skeleton } from 'antd';
import { faCogs, faRocket, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { stateColor } from '../ChangeInstances/List/constants';

/**
 * The two figures cards: one for change instances, one for service items.
 *
 * The numbers per state, and nothing else — no total and no proportion bar.
 * Each figure is a link into the list it counts, filtered to that state.
 */

export interface DashboardMetricsProps {
    /** the `basics` payload; undefined while it is still in flight */
    data?: any;
    loading?: boolean;
    pov: 'CONSUMER' | 'SERVICE_OWNER';
}

interface Figure {
    key: string;
    label: string;
    value: number;
    color: string;
    to: string;
}

const MetricCard = ({
    title,
    icon,
    figures,
    loading,
}: {
    title: string;
    icon: IconDefinition;
    figures: Figure[];
    loading?: boolean;
}) => (
    <Card className="dash-metric" bordered={false}>
        <div className="dash-metric__head">
            <span className="dash-metric__ico" aria-hidden="true">
                <FontAwesomeIcon icon={icon} />
            </span>
            <span className="dash-metric__title">{title}</span>
        </div>

        {loading ? (
            <Skeleton active paragraph={{ rows: 1, width: ['100%'] }} title={false} />
        ) : (
            <div className="dash-figures">
                {figures.map((figure) => (
                    <Link
                        className="dash-figure"
                        key={figure.key}
                        to={figure.to}
                        style={{ ['--figure-accent' as any]: figure.color }}
                    >
                        <span className="dash-figure__label">{figure.label}</span>
                        <span className="dash-figure__value">{figure.value}</span>
                    </Link>
                ))}
            </div>
        )}
    </Card>
);

const DashboardMetrics = ({ data, loading, pov }: DashboardMetricsProps) => {
    const changes = pov === 'CONSUMER' ? 'change_instances' : 'owner_change_instances';
    const items = pov === 'CONSUMER' ? 'service_items' : 'owner_service_items';

    // the lists read their filters from a JSON `filter` query param
    const changesTo = (state: string) => `/${changes}?filter=${encodeURIComponent(JSON.stringify({ state }))}`;
    const itemsTo = (runtime_state: string) => `/${items}?filter=${encodeURIComponent(JSON.stringify({ runtime_state }))}`;

    return (
        <div className="dash-metrics nx-stagger">
            <MetricCard
                title="Change instances"
                icon={faCogs}
                loading={loading}
                figures={[
                    { key: 'pending', label: 'Pending', value: data?.cis_pending ?? 0, color: stateColor.PENDING, to: changesTo('PENDING') },
                    { key: 'approved', label: 'Approved', value: data?.cis_approved ?? 0, color: stateColor.APPROVED, to: changesTo('APPROVED') },
                    { key: 'completed', label: 'Completed', value: data?.cis_completed ?? 0, color: stateColor.COMPLETED, to: changesTo('COMPLETED') },
                ]}
            />

            <MetricCard
                title="Service items"
                icon={faRocket}
                loading={loading}
                figures={[
                    { key: 'in_service', label: 'In service', value: data?.sis_in_service ?? 0, color: stateColor.COMPLETED, to: itemsTo('IN_SERVICE') },
                ]}
            />
        </div>
    );
};

export default DashboardMetrics;
